import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./KeylinePaywall.module.scss"

/**
 * KeylinePaywall (`/keyline`) — an original, self-contained EXPIRATION / renewal
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for this
 * run: EXPIRATION.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — told through the one
 * gesture the security cluster has never used: your PATTERN. The private line
 * only your hand knows how to draw. (Pattern Lock is a real Premium unlock
 * method, so the mechanic is also a real feature.)
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "The way in only your hand knows." Your memories live as a 3×3 grid — the
 * pattern-lock grid, but every cell is one of your own photos. Your term lapsed,
 * so the pattern went quiet: the grid sits dim and unlinked, protection paused.
 * Nothing is lost — every memory is still here. TRACE your secret pattern and a
 * line of light connects the memories in order, each warms to full colour under
 * your finger, a protective frame seals around the whole grid (SEALED · ONLY
 * YOUR PATTERN), and your real safeguards come back on watch. Yearly keeps the
 * line drawn for a whole year; monthly is a small 30-day re-trace.
 *
 * ── Divergence (vs. the 95+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from every security neighbour:
 * Layout      : the PATTERN-LOCK GRID itself — a 3×3 of your memory photos with a
 *               traced line of light over them. Not rings (Sanctum), not a cipher
 *               wall (Static), not a darkroom (Safelight), not a crest (Ward), not
 *               a passcode pad (Only You), not a geofence scope (Perimeter).
 * Interaction : TRACE YOUR PATTERN — a genuine 2-D finger-trace that snaps node to
 *               node in sequence, with a live segment following your finger. New
 *               to the whole set: not a pull-cord (Safelight), not a drag-slider
 *               (Static), not a switch (Standby), not a press-hold (Ward), not a
 *               tap-pulse (Vigil), not a handover slider (Bystander).
 * Metaphor    : YOUR PATTERN — the line that lives nowhere but your memory, tied
 *               to the real Pattern Lock feature.
 * Motion      : RE-LINK — light travels the drawn line, each memory blooms warm as
 *               it's connected, a protective frame closes, guards light on watch.
 * Feature     : real safeguards come back on watch as the pattern completes.
 * Value frame : REINSTATEMENT + sole-access — the connection only your hand can
 *               redraw; calm, not fearful.
 * Register    : quiet intimacy / small wonder — the private line only you know.
 * CTA         : "Re-arm your vault" — yearly = a full year connected, featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (only-you lock with
 * pattern / Face ID / code · encrypted Cloud Vault · Decoy Vault · break-in
 * snapshots · app disguise). The expiration framing states plainly that NOTHING
 * is deleted — every memory is still on this device; only the extra protection
 * paused — so there is no data-loss threat. No countdown, no fabricated scarcity,
 * no misleading claim. "Save 79%" is real arithmetic from live product data.
 * Yearly is the featured, default, most prominent price; monthly is small and
 * secondary. The 7-day free trial is the real trial. Renders on built-in debug
 * product data with bundled photos — no native injection, no external egress.
 */

// The 3×3 grid of memories (row-major). Bundled, self-contained photos reused
// from the repo.
const CELLS = [
  "/keyline/k1.jpg",
  "/keyline/k2.jpg",
  "/keyline/k3.jpg",
  "/keyline/k4.jpg",
  "/keyline/k5.jpg",
  "/keyline/k6.jpg",
  "/keyline/k7.jpg",
  "/keyline/k8.jpg",
  "/keyline/k9.jpg",
]

// "Your pattern": a clean serpentine that reconnects every memory in one stroke.
//   0 1 2
//   3 4 5   →   0 1 2 5 4 3 6 7 8
//   6 7 8
const PATH = [0, 1, 2, 5, 4, 3, 6, 7, 8]

// The real Private Photo Vault safeguards — back "on watch" when the pattern
// completes.
const GUARDS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Pattern, Face ID or a code only you know",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup of every memory",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "a silent photo of anyone who tries a wrong code",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "hide the whole vault behind an ordinary app",
  },
]

// Snap radius (px) for advancing to the next node while tracing.
const SNAP_R = 40

function nextYearLabel() {
  const d = new Date()
  const target = new Date(d.getFullYear() + 1, d.getMonth(), 1)
  const month = target.toLocaleString("en-US", { month: "short" })
  return `${month} ${target.getFullYear()}`
}

type Pt = { x: number; y: number }

const KeylinePaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Match the safe-area insets to the private night backdrop so the scene runs
  // edge to edge instead of sitting inside pale status-bar bands.
  useEffect(() => {
    actions.setHeaderInsetColor("#0a0c16")
    actions.setFooterInsetColor("#070812")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const throughLabel = useMemo(() => nextYearLabel(), [])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── trace state ─────────────────────────────────────────────────────────
  // progress = number of connected nodes along PATH (0..PATH.length).
  const [progress, setProgress] = useState(0)
  const progressRef = useRef(0)
  const [live, setLive] = useState<Pt | null>(null) // live segment tip (finger)
  const dragging = useRef(false)
  const userActed = useRef(false)
  const demoRaf = useRef<number | null>(null)

  const armed = progress >= PATH.length

  // Measured node centres (px, relative to the grid overlay) for the SVG line.
  const gridRef = useRef<HTMLDivElement | null>(null)
  const nodeRefs = useRef<Array<HTMLDivElement | null>>([])
  const [centers, setCenters] = useState<Pt[]>([])
  const centersRef = useRef<Pt[]>([])

  const measure = useCallback(() => {
    const grid = gridRef.current
    if (!grid) return
    const gr = grid.getBoundingClientRect()
    const next: Pt[] = CELLS.map((_, i) => {
      const el = nodeRefs.current[i]
      if (!el) return { x: 0, y: 0 }
      const r = el.getBoundingClientRect()
      return {
        x: r.left - gr.left + r.width / 2,
        y: r.top - gr.top + r.height / 2,
      }
    })
    centersRef.current = next
    setCenters(next)
  }, [])

  useLayoutEffect(() => {
    measure()
    const ro = new ResizeObserver(() => measure())
    if (gridRef.current) ro.observe(gridRef.current)
    window.addEventListener("resize", measure)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [measure])

  const setProgressVal = useCallback((v: number) => {
    const c = Math.max(0, Math.min(PATH.length, v))
    progressRef.current = c
    setProgress(c)
  }, [])

  const stopDemo = useCallback(() => {
    if (demoRaf.current != null) cancelAnimationFrame(demoRaf.current)
    demoRaf.current = null
  }, [])

  // Auto-demo: draw the full pattern once so the payoff reads at a glance, then
  // hand the trace to the user. Respects reduced-motion (snaps armed).
  const playDemo = useCallback(() => {
    stopDemo()
    if (reduceMotion) {
      setProgressVal(PATH.length)
      return
    }
    const perNode = 150 // ms between connected nodes
    const start = performance.now()
    const step = (now: number) => {
      if (userActed.current) return
      const elapsed = now - start
      const reached = Math.min(PATH.length, 1 + Math.floor(elapsed / perNode))
      setProgressVal(reached)
      // live tip glides toward the next node for a hand-drawn feel
      const cs = centersRef.current
      if (reached < PATH.length && cs.length) {
        const from = cs[PATH[reached - 1]]
        const to = cs[PATH[reached]]
        const t = Math.min(1, (elapsed % perNode) / perNode)
        if (from && to)
          setLive({
            x: from.x + (to.x - from.x) * t,
            y: from.y + (to.y - from.y) * t,
          })
      } else {
        setLive(null)
      }
      if (reached < PATH.length) demoRaf.current = requestAnimationFrame(step)
      else {
        setLive(null)
        demoRaf.current = null
      }
    }
    demoRaf.current = requestAnimationFrame(step)
  }, [reduceMotion, setProgressVal, stopDemo])

  useEffect(() => {
    // `?nodemo` skips the teaching auto-play (used only for pristine "before"
    // marketing captures); the live product always plays it.
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) playDemo()
    }, 700)
    return () => {
      window.clearTimeout(t)
      stopDemo()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // ── manual trace ──────────────────────────────────────────────────────────
  const localPoint = useCallback((e: React.PointerEvent): Pt => {
    const grid = gridRef.current
    if (!grid) return { x: 0, y: 0 }
    const gr = grid.getBoundingClientRect()
    return { x: e.clientX - gr.left, y: e.clientY - gr.top }
  }, [])

  const beginTrace = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      stopDemo()
      dragging.current = true
      setProgressVal(1) // grab the start node
      setLive(localPoint(e))
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    },
    [localPoint, setProgressVal, stopDemo],
  )

  const moveTrace = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      const p = localPoint(e)
      setLive(p)
      const cs = centersRef.current
      let reached = progressRef.current
      // advance while the finger reaches successive nodes in order
      while (reached < PATH.length) {
        const target = cs[PATH[reached]]
        if (!target) break
        const dx = p.x - target.x
        const dy = p.y - target.y
        if (dx * dx + dy * dy <= SNAP_R * SNAP_R) reached += 1
        else break
      }
      if (reached !== progressRef.current) setProgressVal(reached)
      if (reached >= PATH.length) {
        dragging.current = false
        setLive(null)
      }
    },
    [localPoint, setProgressVal],
  )

  const endTrace = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    setLive(null)
    if (progressRef.current < PATH.length) {
      // incomplete → let it fade back so the user can try the whole line again
      setProgressVal(0)
    }
  }, [setProgressVal])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // Build the SVG polyline points for the connected portion + live segment.
  const linePts = useMemo(() => {
    const cs = centers
    if (!cs.length) return [] as Pt[]
    const pts: Pt[] = []
    const n = Math.min(progress, PATH.length)
    for (let i = 0; i < n; i++) {
      const c = cs[PATH[i]]
      if (c) pts.push(c)
    }
    return pts
  }, [centers, progress])

  const connectedSet = useMemo(() => {
    const s = new Set<number>()
    for (let i = 0; i < progress; i++) s.add(PATH[i])
    return s
  }, [progress])

  const polyPoints =
    linePts.length > 0
      ? linePts
          .map((p) => `${p.x},${p.y}`)
          .concat(live && dragging.current ? [`${live.x},${live.y}`] : [])
          .join(" ")
      : ""

  const gw = gridRef.current?.clientWidth ?? 0
  const gh = gridRef.current?.clientHeight ?? 0

  return (
    <div
      className={`${styles.root} ${armed ? styles.isArmed : ""}`}
      style={{ ["--p" as any]: progress / PATH.length }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            {armed
              ? "Pattern lock · re-armed"
              : "Pattern lock · protection paused"}
          </span>
          <h1 className={styles.title}>
            {armed ? (
              <>
                Re-armed. <span className={styles.accent}>Only your line</span>
                <br />
                opens it.
              </>
            ) : (
              <>
                Your line <span className={styles.dim}>went quiet.</span>
                <br />
                Your memories are still here.
              </>
            )}
          </h1>
        </section>

        {/* ── the pattern grid (hero) ────────────────────────────────────── */}
        <section className={styles.stage}>
          <div
            className={styles.grid}
            ref={gridRef}
            onPointerDown={beginTrace}
            onPointerMove={moveTrace}
            onPointerUp={endTrace}
            onPointerCancel={endTrace}
          >
            {CELLS.map((src, i) => {
              const on = connectedSet.has(i)
              const order = PATH.indexOf(i)
              const isStart = order === 0
              return (
                <div
                  key={i}
                  ref={(el) => (nodeRefs.current[i] = el)}
                  className={`${styles.cell} ${on ? styles.cellOn : ""}`}
                  style={{ ["--i" as any]: order }}
                >
                  <span
                    className={styles.photo}
                    style={{ backgroundImage: `url(${src})` }}
                  />
                  <span className={styles.cellTint} aria-hidden="true" />
                  <span className={styles.node} aria-hidden="true">
                    <span className={styles.nodeDot} />
                  </span>
                  {isStart && !armed && progress === 0 && (
                    <span className={styles.startHint} aria-hidden="true">
                      <span className={styles.startRing} />
                    </span>
                  )}
                </div>
              )
            })}

            {/* the traced line of light */}
            <svg
              className={styles.trace}
              width={gw}
              height={gh}
              viewBox={`0 0 ${gw || 1} ${gh || 1}`}
              aria-hidden="true"
            >
              {polyPoints && (
                <>
                  <polyline
                    className={styles.traceGlow}
                    points={polyPoints}
                    fill="none"
                  />
                  <polyline
                    className={styles.traceLine}
                    points={polyPoints}
                    fill="none"
                  />
                </>
              )}
            </svg>

            {/* protective frame that seals when the pattern completes */}
            <span
              className={`${styles.frame} ${armed ? styles.frameOn : ""}`}
              aria-hidden="true"
            />
          </div>

          {/* seal plate */}
          <div
            className={`${styles.plate} ${armed ? styles.plateOn : styles.plateOff}`}
          >
            <span className={styles.plateDot} />
            {armed
              ? "SEALED · ONLY YOUR PATTERN"
              : "UNLINKED · PROTECTION PAUSED"}
          </div>

          <p className={styles.traceHint}>
            {armed ? "Traced again to replay" : "Trace your pattern to re-arm"}
          </p>
        </section>

        {/* ── reassurance (honest expiration framing) ────────────────────── */}
        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Nothing was deleted — every memory is still on this device. Renewing
          simply switches your Premium protection back on.
        </p>

        {/* ── guards on watch (real safeguards) ──────────────────────────── */}
        <ul className={styles.guards}>
          <li className={styles.guardsHead}>
            <span>Back on watch</span>
            <span className={styles.guardsCount}>
              {armed ? `${GUARDS.length}/${GUARDS.length} on` : "paused"}
            </span>
          </li>
          {GUARDS.map((g) => (
            <li
              key={g.key}
              className={`${styles.guard} ${armed ? styles.guardOn : ""}`}
            >
              <span className={styles.guardIcon} aria-hidden="true">
                <g.Icon width={16} height={16} />
              </span>
              <span className={styles.guardText}>
                <span className={styles.guardName}>{g.name}</span>
                <span className={styles.guardLine}>{g.line}</span>
              </span>
              <span className={styles.guardChip} aria-hidden="true">
                {armed ? "ON WATCH" : "•"}
              </span>
            </li>
          ))}
        </ul>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Stay connected · 1 year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyThru}>
              Your line stays drawn through {throughLabel}
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or a 30-day re-trace</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Re-arm your vault — ${trialDays}-day free trial`
              : "Re-arm your vault"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default KeylinePaywall
